import { ReactElement, useEffect } from 'react';
import { GetStaticPropsResult } from 'next';
import { useRouter } from 'next/router';

import { Box, BREAK_POINT } from '@bucketplace/design-system';
import { WebviewLayout } from '@bucketplace/owap-sdk';
import { useImpressionLog } from '@bucketplace/bucket-log';

import FeedHead from 'components/Layout/FeedHead';
import { RebrandPageLayout, SimpleBrandHeader } from 'brand/common';
import { CLOUD_SECTION_ASSETS_URL } from 'brand/components/landing/constants';
import { RebrandContent, AtlasEntry } from 'brand/components/landing';
import { useOwapInfo } from 'hooks';
import { getAppOsInfo } from 'utils';

interface RebrandPageProps {
  mobilePositions: AtlasEntry[];
  pcPositions: AtlasEntry[];
}

export default function RebrandPage({ mobilePositions, pcPositions }: RebrandPageProps): ReactElement {
  const router = useRouter();
  const { osType } = getAppOsInfo() ?? {};
  const { isOwap } = useOwapInfo();
  const { ref } = useImpressionLog({});

  useEffect(() => {
    let prevIsMobile = window.innerWidth < BREAK_POINT.laptop;

    const observer = new ResizeObserver(() => {
      const currentIsMobile = window.innerWidth < BREAK_POINT.laptop;

      if (prevIsMobile !== currentIsMobile) {
        window.scrollTo(0, 0);
        router.reload();
      }

      prevIsMobile = currentIsMobile;
    });

    observer.observe(document.documentElement);
    return () => observer.disconnect();
  }, [router]);

  const ContentElement = (
    <Box ref={ref}>
      <FeedHead
        title="모두, 오늘의집에 산다"
        webviewTitle="어서오세요, 새로운 오늘의집"
        description="새로운 오늘의집을 만나보세요!"
        ogTitle="모두, 오늘의집에 산다"
        ogDescription="새로운 오늘의집을 만나보세요!"
        image="https://prs.ohousecdn.com/apne2/events/rebranding/v1-419639508697216.png"
        pageId="REBRANDING_INTRODUCTION"
      />
      <RebrandContent mobilePositions={mobilePositions} pcPositions={pcPositions} />
    </Box>
  );

  if (isOwap) {
    return (
      <WebviewLayout
        isApp
        isSafeArea={osType === 'iOS'}
        type="fullpage"
        appBar={<SimpleBrandHeader />}
      >
        { ContentElement }
      </WebviewLayout>
    );
  }

  return ContentElement;
}

RebrandPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <RebrandPageLayout>
      { page }
    </RebrandPageLayout>
  );
};

export async function getStaticProps(): Promise<GetStaticPropsResult<RebrandPageProps>> {
  try {
    // 빌드 시 미리 데이터 가져오기
    const [mobileRes, pcRes] = await Promise.all([
      fetch(CLOUD_SECTION_ASSETS_URL.positions.mobile),
      fetch(CLOUD_SECTION_ASSETS_URL.positions.pc),
    ]);

    const [mobilePositions, pcPositions] = await Promise.all([
      mobileRes.json(),
      pcRes.json(),
    ]);

    // Priority atlas만 CDN 캐시 워밍 (빠른 초기 렌더링용)
    try {
      const priorityUrls = [
        ...CLOUD_SECTION_ASSETS_URL.atlas.mobile.priority,
        ...CLOUD_SECTION_ASSETS_URL.atlas.pc.priority,
      ];

      // HEAD 요청으로 가벼운 캐시 워밍
      await Promise.allSettled(
        priorityUrls.map(url =>
          fetch(url, { method: 'HEAD' }).catch(() => undefined)
        )
      );
    } catch (cacheWarmError) {
      // 캐시 워밍 실패해도 페이지는 생성
    }

    return {
      props: {
        mobilePositions,
        pcPositions,
      },

      // 하루마다 재생성 (CDN 캐시 워밍 갱신)
      revalidate: 86400,
    };
  } catch (error) {
    console.error('Failed to fetch positions data:', error);

    // 에러 발생시 빈 배열로 fallback
    return {
      props: {
        mobilePositions: [],
        pcPositions: [],
      },
      revalidate: 1, // 에러시 1초 후 재시도
    };
  }
}
